import React from 'react';
import PropTypes from 'prop-types';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { ErrorMessage } from 'formik';
import { Message } from 'primereact/message';
import { Checkbox } from 'primereact/checkbox';
import { RadioButton } from 'primereact/radiobutton';
import { MultiSelect } from 'primereact/multiselect';
import { Chips } from 'primereact/chips';
import { Calendar } from 'primereact/calendar';
import { injectIntl } from 'react-intl';
import { formikFormShape, formikFieldShape } from './prop-types';

const shouldDisplayError = (field, touched, errors) => {
  if (field.name.includes('.')) {
    const prefix = field.name.slice(0, field.name.indexOf('.'));
    const suffix = field.name.slice(field.name.indexOf('.') + 1, field.name.length);

    const touchedField = touched[prefix];
    const fieldWithError = errors[prefix];

    if (touchedField && fieldWithError) {
      return touchedField[suffix] && fieldWithError[suffix];
    }

    return false;
  }

  return touched[field.name] && errors[field.name];
};

export const DropdownComponent = ({ field, form: { touched, errors }, ...props }) => {
  const displayError = touched[field.name] && errors[field.name];
  const { className, hasError } = props;

  const error = hasError !== undefined ? hasError : displayError;

  const inputClassName = getInputClassName(error, 'input-error', className);

  return <Dropdown {...field} {...props} className={inputClassName} />;
};

DropdownComponent.propTypes = {
  field: formikFieldShape.isRequired,
  form: formikFormShape.isRequired,
};

export const InputTextComponent = ({ field, form: { touched, errors }, labelText, className, ...props }) => {
  const displayError = touched[field.name] && errors[field.name];

  const inputClassName = getInputClassName(displayError, 'input-error', className);

  return <InputText {...field} {...props} className={inputClassName} />;
};

InputTextComponent.propTypes = {
  field: formikFieldShape.isRequired,
  form: formikFormShape.isRequired,
  labelText: PropTypes.string.isRequired,
};

export const TextAreaComponent = ({ field, form: { touched, errors }, ...props }) => {
  const displayError = touched[field.name] && errors[field.name];
  const value = field.value ? field.value : '';
  return <InputTextarea {...field} {...props} value={value} className={displayError ? 'input-error' : ''} />;
};

TextAreaComponent.propTypes = {
  field: formikFieldShape.isRequired,
  form: formikFormShape.isRequired,
};

export const PrimeReactErrorMessage = ({ msg }) => <Message severity="error" text={msg} />;

PrimeReactErrorMessage.propTypes = {
  msg: PropTypes.string.isRequired,
};

export const InputTextWithValidationMessageAndToolip = ({
  field,
  form: { touched, errors },
  labelText,
  placeholderText,
  tooltipText,
  ...props
}) => {
  const displayError = shouldDisplayError(field, touched, errors);

  if (placeholderText) {
    return (
      <>
        <div>
          <label htmlFor={field.id}>{labelText}</label>
        </div>
        <InputText
          {...field}
          {...props}
          placeholder={placeholderText}
          className={displayError ? 'p-error' : ''}
          style={{ width: '25em', marginRight: '.25em' }}
        />
        <i className="fas fa-question-circle" title={tooltipText} />
      </>
    );
  }

  return (
    <>
      <span className="p-float-label">
        <InputText
          {...field}
          {...props}
          className={displayError ? 'p-error' : ''}
          style={{ width: '25em', marginRight: '.25em' }}
        />
        <label htmlFor={field.id}>{labelText}</label>
        <i className="fas fa-question-circle" title={tooltipText} />
      </span>
    </>
  );
};

InputTextWithValidationMessageAndToolip.propTypes = {
  field: formikFieldShape.isRequired,
  form: formikFormShape.isRequired,
  labelText: PropTypes.string.isRequired,
  tooltipText: PropTypes.string.isRequired,
};

export const CommissionPercentageInput = ({
  field, // { name, value, onChange, onBlur }
  form: { touched, errors }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
  ...props
}) => {
  const displayError = touched[field.name] && errors[field.name];

  return (
    <div>
      <InputText {...field} {...props} className={displayError ? 'p-error' : ''} />
    </div>
  );
};

CommissionPercentageInput.propTypes = {
  field: formikFieldShape.isRequired,
  form: formikFormShape.isRequired,
  labelText: PropTypes.string.isRequired,
};

export const InputTextWithValidationMessage = ({
  field, // { name, value, onChange, onBlur }
  form: { touched, errors }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
  labelText,
  placeholderText,
  ...props
}) => {
  const displayError = touched[field.name] && errors[field.name];

  if (!!placeholderText) {
    return (
      <>
        <div>
          <label htmlFor={field.id}>{labelText}</label>
        </div>
        <InputText
          {...field}
          {...props}
          placeholder={placeholderText}
          className={displayError ? 'p-error' : ''}
          style={{ marginRight: '.25em' }}
        />

        <ErrorMessage name={field.name} component="div" />
      </>
    );
  }

  return (
    <>
      <span className="p-float-label">
        <InputText {...field} {...props} className={displayError ? 'p-error' : ''} style={{ marginRight: '.25em' }} />

        <label htmlFor={field.id}>{labelText}</label>
      </span>
      <ErrorMessage name={field.name} component="div" />
    </>
  );
};

InputTextWithValidationMessage.propTypes = {
  field: formikFieldShape.isRequired,
  form: formikFormShape.isRequired,
  labelText: PropTypes.string.isRequired,
};

export const InputPasswordWithValidationMessage = ({
  field, // { name, value, onChange, onBlur }
  form: { touched, errors }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
  labelText,
  ...props
}) => {
  const displayError = touched[field.name] && errors[field.name];

  return (
    <>
      <span className="p-float-label">
        <Password
          feedback={false}
          {...field}
          {...props}
          className={displayError ? 'p-error' : ''}
          style={{ marginRight: '.25em' }}
        />

        <label htmlFor={field.id}>{labelText}</label>
      </span>
      <ErrorMessage name={field.name} component="div" />
    </>
  );
};

InputPasswordWithValidationMessage.propTypes = {
  field: formikFieldShape.isRequired,
  form: formikFormShape.isRequired,
  labelText: PropTypes.string.isRequired,
};

export const DropdownWithValidationAndTooltip = ({ field, form: { touched, errors }, tooltipText, ...props }) => {
  const displayError = touched[field.name] && errors[field.name];
  return (
    <>
      <Dropdown
        {...field}
        {...props}
        className={displayError ? 'p-error' : ''}
        style={{ width: '25em', marginRight: '.25em' }}
      />
      <i className="fas fa-question-circle" title={tooltipText} />
      {errors[field.name] && <PrimeReactErrorMessage msg={errors[field.name]} />}
    </>
  );
};

DropdownWithValidationAndTooltip.propTypes = {
  field: formikFieldShape.isRequired,
  form: formikFormShape.isRequired,
  tooltipText: PropTypes.string.isRequired,
};

export const DropdownWithValidation = ({ field, form: { touched, errors }, ...props }) => {
  const displayError = touched[field.name] && errors[field.name];
  return (
    <React.Fragment>
      <Dropdown {...field} {...props} className={displayError ? 'p-error' : ''} style={{ marginRight: '.25em' }} />
      {errors[field.name] && <PrimeReactErrorMessage msg={errors[field.name]} />}
    </React.Fragment>
  );
};

DropdownWithValidation.propTypes = {
  field: formikFieldShape.isRequired,
  form: formikFormShape.isRequired,
};

export const TextAreaWithValidation = ({ field, form: { touched, errors }, className, ...props }) => {
  const displayError = touched[field.name] && errors[field.name];
  const value = field.value ? field.value : '';

  const inputClassName = getInputClassName(displayError, 'p-error', className);

  return (
    <div>
      <InputTextarea {...field} {...props} value={value} className={inputClassName} style={{ marginRight: '.25em' }} />
    </div>
  );
};

TextAreaWithValidation.propTypes = {
  field: formikFieldShape.isRequired,
  form: formikFormShape.isRequired,
};

export const LegacyInputTextComponent = ({ field, form: { touched, errors }, ...props }) => {
  const displayError = touched[field.name] && errors[field.name];
  return (
    <React.Fragment>
      <InputText {...field} {...props} className={displayError ? 'p-error' : ''} style={{ marginRight: '.25em' }} />
      <ErrorMessage name={field.name} render={(msg) => <PrimeReactErrorMessage msg={msg} />} />
    </React.Fragment>
  );
};

LegacyInputTextComponent.propTypes = {
  field: formikFieldShape.isRequired,
  form: PropTypes.objectOf(PropTypes.any).isRequired,
};

export const InputNumberComponent = ({ field, form: { touched, errors }, className, ...props }) => {
  const displayError = touched[field.name] && errors[field.name];
  const inputClassName = getInputClassName(displayError, 'p-error', className);

  return <InputText type="number" min={0} {...field} {...props} className={inputClassName} />;
};

InputNumberComponent.propTypes = {
  field: formikFieldShape.isRequired,
  form: PropTypes.objectOf(PropTypes.any).isRequired,
};

export const LegacyInputPasswordComponent = ({ field, form: { touched, errors }, ...props }) => {
  const displayError = touched[field.name] && errors[field.name];
  return (
    <React.Fragment>
      <Password
        feedback={false}
        {...field}
        {...props}
        className={displayError ? 'p-error' : ''}
        style={{ marginRight: '.25em' }}
      />
      <ErrorMessage name={field.name} render={(msg) => <PrimeReactErrorMessage msg={msg} />} />
    </React.Fragment>
  );
};

LegacyInputPasswordComponent.propTypes = {
  field: formikFieldShape.isRequired,
  form: PropTypes.objectOf(PropTypes.any).isRequired,
};

export const FormikCheckbox = ({
  field,
  form: { touched, errors },
  id,
  labelText,
  setFieldValue,
  additionAction,
  ...props
}) => {
  const displayError = touched[field.name] && errors[field.name];

  return (
    <>
      <Checkbox
        checked={!!field.value}
        {...field}
        {...props}
        inputId={id}
        onChange={(eventParams) => {
          const { checked } = eventParams;
          setFieldValue(field.name, checked);
          if (additionAction) {
            additionAction(eventParams, setFieldValue);
          }
        }}
        className={displayError ? 'input-error' : ''}
      />
      {labelText && (
        <label htmlFor={id} className="p-checkbox-label padding-left-0-25">
          {labelText}
        </label>
      )}
    </>
  );
};

FormikCheckbox.propTypes = {
  additionAction: PropTypes.func,
  field: formikFieldShape.isRequired,
  form: PropTypes.objectOf(PropTypes.any).isRequired,
  setFieldValue: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  labelText: PropTypes.string,
};

FormikCheckbox.defaultProps = {
  labelText: '',
  additionAction: null,
};

export const FormikRadio = injectIntl(
  ({
    field,
    form: { touched, errors },
    id,
    labeTextId,
    labelDefaultText,
    setFieldValue,
    inputValue,
    intl: { formatMessage },
    ...props
  }) => {
    const displayError = touched[field.name] && errors[field.name];
    const { value } = field;
    const checked = value === inputValue;
    const labelText = formatMessage({
      id: labeTextId,
      defaultMessage: labelDefaultText,
    });

    return (
      <React.Fragment>
        <div>
          <RadioButton
            {...field}
            {...props}
            inputId={id}
            checked={checked}
            className={displayError ? 'has-error' : ''}
          />
          <label htmlFor={id} className="p-radiobutton-label">
            {labelText}
          </label>
        </div>

        <ErrorMessage name={field.name} component="div" />
      </React.Fragment>
    );
  },
);

FormikRadio.propTypes = {
  field: formikFieldShape.isRequired,
  form: PropTypes.objectOf(PropTypes.any).isRequired,
  inputValue: PropTypes.string.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  labeTextId: PropTypes.string,
  labelDefaultText: PropTypes.string,
};

FormikRadio.defaultProps = {
  labeTextId: '',
  labelDefaultText: '',
};

export const FormikMultiSelect = ({ field, form: { touched, errors }, setFieldValue, className, ...rest }) => {
  const displayError = touched[field.name] && errors[field.name];
  const inputClassName = getInputClassName(displayError, 'p-error', className);

  return <MultiSelect {...field} {...rest} className={inputClassName} />;
};

FormikMultiSelect.propTypes = {
  field: formikFieldShape.isRequired,
  form: formikFormShape.isRequired,
  setFieldValue: PropTypes.func.isRequired,
};

export const FormikChips = ({ field, form: { errors }, setFieldValue, ...rest }) => {
  const { name, value = [] } = field;
  return (
    <div>
      <Chips
        {...field}
        {...rest}
        value={value}
        onChange={(e) => {
          setFieldValue(name, e.value);
        }}
      />
      {errors[field.name] && <PrimeReactErrorMessage msg={errors[field.name]} />}
    </div>
  );
};

FormikChips.propTypes = {
  field: formikFieldShape.isRequired,
  form: formikFormShape.isRequired,
  setFieldValue: PropTypes.func.isRequired,
};

export const FormikCalendar = ({ field, form: { errors }, ...rest }) => (
  <div>
    <Calendar {...field} {...rest} />
    {errors[field.name] && <PrimeReactErrorMessage msg={errors[field.name]} />}
  </div>
);

FormikCalendar.propTypes = {
  field: formikFieldShape.isRequired,
  form: formikFormShape.isRequired,
};

const getInputClassName = (error, errorClassName, className) => {
  if (!error && !className) return '';

  if (!error && className) return className;

  if (error && className) return `${errorClassName} ${className}`;

  if (error && !className) return errorClassName;

  return '';
};
